import { AnimatePresence } from "framer-motion";
import * as S from "./menu.styles";
import type { MenuProps } from "./types";
import { faCircle, faClose } from "@fortawesome/free-solid-svg-icons";
import { menuOptions } from "./constants";
import { MenuOptions } from "../menu-options/menu-options";
import { useEffect, useRef } from "react";
import { Logo } from "../../../../../../components/logo/logo";
import { useCategorizedPackages } from "../../../../../../hooks/use-categorized-packages";
import { MenuFooter } from "../menu-footer/menu-footer";

export const MobileMenu = ({ isOpen, onClose }: MenuProps) => {
    const { categories } = useCategorizedPackages();

    const scrollRef = useRef(0);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const body = document.body;
        if (isOpen) {
            scrollRef.current = window.scrollY;
            body.style.top = `-${scrollRef.current}px`;
            body.classList.add("body-lock");
        } else {
            body.classList.remove("body-lock");
            body.style.top = "";
            window.scrollTo(0, scrollRef.current);
        }
        return () => {
            body.classList.remove("body-lock");
            body.style.top = "";
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClick = (e: MouseEvent) => {
            const menu = menuRef.current;
            const target = e.target as HTMLElement;

            if (target.closest("[data-cart-drawer]")) return;

            if (menu && !menu.contains(target)) {
                e.stopImmediatePropagation();
                e.preventDefault();
                onClose();

                document.body.style.pointerEvents = "none";
                setTimeout(() => {
                    document.body.style.pointerEvents = "";
                }, 200);
            }
        };

        document.addEventListener("click", handleClick, true);
        return () => {
            document.removeEventListener("click", handleClick, true);
        };
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <S.Overlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    />

                    <S.MenuContainer
                        ref={menuRef}
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", stiffness: 260, damping: 25 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <S.Header>
                            <div className="logo">
                                <Logo />
                                <h2>Menu</h2>
                            </div>
                            <button onClick={onClose} aria-label="Fechar menu">
                                <S.Icon icon={faClose} />
                            </button>
                        </S.Header>

                        <S.ScrollableContent>
                            <S.Nav>
                                <h3>Navegação</h3>
                                <ul>
                                    {menuOptions.map((option, index) => (
                                        <li key={index}>
                                            <MenuOptions {...option} onClose={onClose} />
                                        </li>
                                    ))}
                                </ul>
                            </S.Nav>

                            <S.Nav>
                                <h3>Categorias</h3>
                                <ul>
                                    {categories.map((option, index) => (
                                        <li key={index}>
                                            <MenuOptions
                                                icon={faCircle}
                                                label={option.name}
                                                url={`/produtos?categoria=${option.slug}`}
                                                customIconSize={0.5}
                                                onClose={onClose}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </S.Nav>
                        </S.ScrollableContent>

                        <S.Footer>
                            <MenuFooter onClose={onClose} />
                        </S.Footer>
                    </S.MenuContainer>
                </>
            )}
        </AnimatePresence>
    );
};
